import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { fetchLocale, saveLocale } from '../../services/CommonServices';
import useAuth from '../../hooks/useAuth';
import { useBuilder } from '../../contexts/BuilderContext';
import { useInstance } from '../../contexts/InstanceContext';

function IntlObject({ keycode, returnType, isLogin, containerClass, containerStyle }) {
    const intl = useIntl();
    const { isLoggedIn } = useAuth();
    const builderContext = useBuilder();
    const isEditMode = builderContext?.isEditMode ?? false;
    const [isEditing, setEditing] = useState(false);
    const [localeData, setLocaleData] = useState({ ko: '', en: '' });
    const { memberId: member_id } = useInstance();
    const koInputRef = useRef(null);
    const enInputRef = useRef(null);

    // isLogin prop이 명시적으로 전달되면 사용, 아니면 자동 감지
    // Edit Mode가 켜져 있을 때만 편집 가능
    const canEdit = (isLogin ?? isLoggedIn) && isEditMode;

    const fetchData = async () => {
        try {
            const res = await fetchLocale(member_id, keycode);
            if (res.data.success && res.data.messages) {
                const koMessage = res.data.messages.ko[keycode];
                const enMessage = res.data.messages.en[keycode];
                setLocaleData({ ko: koMessage, en: enMessage });
            } else {
                console.error('No messages found in response');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const saveData = async () => {
        try {
            const koValue = koInputRef.current.value;
            const enValue = enInputRef.current.value;

            await saveLocale(member_id, keycode, { ko: koValue, en: enValue });
            setEditing(false);
            alert('Saved Successfully!');
            window.location.reload();
        } catch (err) {
            console.error(err);
            alert('Error saving data!');
        }
    };

    const onEdit = () => {
        if (!canEdit) {
            return;
        }
        fetchData();
        setEditing(true);
    };

    const ClickableContainer = styled.div`
        border: ${({ clickable }) => clickable ? '1px dashed lightgray' : 'none'};
        cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};

        &:hover {
            border: ${({ clickable }) => clickable ? '1px dashed gray' : 'none'};
            background-color: ${({ clickable }) => clickable ? 'rgba(220, 220, 220, 0.3)' : 'transparent'};
        }
    `;

    const fadeIn = keyframes`
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    `;

    const fadeOut = keyframes`
        from {
            opacity: 1;
        }
        to {
            opacity: 0;
        }
    `;

    // 스타일 정의
    const FloatingLayer = styled.div`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
        display: flex;
        justify-content: center;
        align-items: center;
        animation: ${props => (props.visible ? fadeIn : fadeOut)} 0.3s ease-in-out;
        z-index: 9999;
    `;

    const StyledForm = styled.div`
        width: 90%;
        max-width: 500px;
        background-color: #ffffff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        display: flex;
        flex-direction: column;
        gap: 24px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    `;

    const FormHeader = styled.div`
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 16px;
        margin-bottom: 8px;
        h3 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #111827;
        }
        p {
            margin: 4px 0 0;
            font-size: 14px;
            color: #6b7280;
        }
    `;

    const InputGroup = styled.div`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `;

    const StyledLabel = styled.label`
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        display: flex;
        align-items: center;
        gap: 8px;

        &::before {
            content: '';
            display: block;
            width: 4px;
            height: 14px;
            background-color: #3b82f6;
            border-radius: 2px;
        }
    `;

    const StyledInput = styled.input`
        width: 100%;
        font-size: 15px;
        padding: 12px 16px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        outline: none;
        transition: all 0.2s ease;
        background-color: #f9fafb;
        color: #1f2937;

        &:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
            background-color: #ffffff;
        }
        
        &::placeholder {
            color: #9ca3af;
        }
    `;

    const ButtonGroup = styled.div`
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 8px;
    `;

    const StyledButton = styled.button`
        font-size: 14px;
        font-weight: 600;
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        
        ${props => props.primary ? `
            background-color: #2563eb;
            color: #ffffff;
            box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
            
            &:hover {
                background-color: #1d4ed8;
                transform: translateY(-1px);
                box-shadow: 0 6px 8px -1px rgba(37, 99, 235, 0.3);
            }
            
            &:active {
                transform: translateY(0);
            }
        ` : `
            background-color: #f3f4f6;
            color: #4b5563;
            border: 1px solid #e5e7eb;
            
            &:hover {
                background-color: #e5e7eb;
                color: #1f2937;
            }
        `}
    `;

    const FloatingInputLayer = ({ visible, localeData, saveData, setEditing, containerClass, containerStyle }) => (
        <FloatingLayer visible={visible}>
            <StyledForm className={containerClass} style={containerStyle}>
                <FormHeader>
                    <h3>Edit Text</h3>
                    <p>Modify the text content for each language.</p>
                </FormHeader>
                
                <InputGroup>
                    <StyledLabel>Korean (KO)</StyledLabel>
                    <StyledInput defaultValue={localeData.ko} ref={koInputRef} placeholder="Enter Korean text..." />
                </InputGroup>
                
                <InputGroup>
                    <StyledLabel>English (EN)</StyledLabel>
                    <StyledInput defaultValue={localeData.en} ref={enInputRef} placeholder="Enter English text..." />
                </InputGroup>

                <ButtonGroup>
                    <StyledButton onClick={() => setEditing(false)}>Cancel</StyledButton>
                    <StyledButton primary onClick={saveData}>Save Changes</StyledButton>
                </ButtonGroup>
            </StyledForm>
        </FloatingLayer>
    );

    const RenderComponent = () => {
        const message = intl.formatMessage({ id: keycode });

        const handleClick = (e) => {
            if (canEdit) {
                e.preventDefault();
                e.stopPropagation();
                onEdit();
            }
        };

        const renderContent = (type) => {
            return (
                <ClickableContainer
                    as={type}
                    clickable={canEdit}
                    onClick={handleClick}
                    dangerouslySetInnerHTML={{ __html: message }}
                />
            );
        };

        // Use React Portal to render modal at document.body level
        // This prevents form validation interference from parent forms
        const modalPortal = isEditing ? ReactDOM.createPortal(
            <FloatingInputLayer
                visible={isEditing}
                localeData={localeData}
                setLocaleData={setLocaleData}
                saveData={saveData}
                setEditing={setEditing}
                containerClass={containerClass}
                containerStyle={containerStyle}
            />,
            document.body
        ) : null;

        return (
            <>
                {modalPortal}
                {(() => {
                    switch (returnType) {
                        case 'p':
                            return renderContent("p");
                        case 'span':
                            return renderContent("span");
                        case 'label':
                            return renderContent("label");
                        case 'div':
                        default:
                            return renderContent("div");
                    }
                })()}
            </>
        );
    };

    return <RenderComponent />;
}

IntlObject.propTypes = {
    keycode: PropTypes.string.isRequired,
    returnType: PropTypes.oneOf(['p', 'span', 'div', 'label']),
    isLogin: PropTypes.bool,
    containerClass: PropTypes.string,
    containerStyle: PropTypes.object,
};

IntlObject.defaultProps = {
    returnType: 'div',
    isLogin: undefined,  // undefined로 설정해야 useAuth의 isLoggedIn이 사용됨
    containerClass: '',
    containerStyle: {},
};

export default IntlObject;
